import {useMemo} from 'react'
import {Trans} from 'react-i18next'
import messages from '../../assets/messages'
import {PammOfferType} from '../../graphql/types'
import {ExpansionProvider} from './components/ExpansionContext'
import {KpiPanelProvider} from './components/KpiPanelContext'
import {MlmInfoBanner} from './components/MlmInfoBanner'
import {NetworkKpiCards} from './components/NetworkKpiCards'
import {NetworkLegend} from './components/NetworkLegend'
import {OfferTreeNode} from './components/OfferTreeNode'
import {TreeControls} from './components/TreeControls'
import {MainOfferControl, MainOfferProvider} from './MainOfferContext'
import {EmptyState, Root, ScrollArea, ScrollHint, TreeInner} from './PammOffersTree.styled'
import {buildOfferTree} from './utils/buildOfferTree'
import {aggregateNetworkStats} from './utils/networkStats'

type PammOffersTreeProps = {
  offers: PammOfferType[]
  /** Passed straight to the summary cards; see `NetworkKpiCards.recalculating` (PT-5717). */
  recalculating?: boolean
  /** The Main Offer choice with its setter; set on the master view only (PT-6881). */
  mainOffer?: MainOfferControl
}

/**
 * Hierarchical, expandable tree view of PAMM offers (PT-5399).
 *
 * Rebuilds the parent/child hierarchy from each offer's agent chain and renders it as
 * nested, collapsible cards, preceded by the network KPI summary, a colour legend and an
 * explanatory banner. Used by both the Money Manager and Agent offer pages.
 *
 * @param offers - Flat list of PAMM offers in the viewer's scope; order does not matter.
 * @param recalculating - Shows placeholders instead of the summary figures during a view switch.
 * @param mainOffer - The Money Manager's Main Offer and its setter; omitted where it does not apply.
 */
export const PammOffersTree = ({offers, recalculating, mainOffer}: PammOffersTreeProps) => {
  // Memoised to avoid rebuilding the whole tree (and the provider's derived node index) on every
  // render; expansion state itself survives re-renders regardless, as it is keyed by stable offer id.
  const roots = useMemo(
    () => buildOfferTree(offers, mainOffer?.offerId),
    [offers, mainOffer?.offerId],
  )
  const stats = useMemo(() => aggregateNetworkStats(roots), [roots])
  const currency = offers.find((offer) => offer.currency)?.currency ?? 'USD'

  if (roots.length === 0) {
    return (
      <EmptyState data-testid="pamm-offers-tree-empty">
        <Trans {...messages.pammNetworkEmpty} />
      </EmptyState>
    )
  }

  return (
    <MainOfferProvider value={mainOffer}>
      <Root>
        <NetworkKpiCards stats={stats} currency={currency} recalculating={recalculating} />
        <MlmInfoBanner />
        <NetworkLegend />
        <ExpansionProvider roots={roots}>
          <TreeControls />
          <ScrollHint>
            <Trans {...messages.pammScrollHint} />
          </ScrollHint>
          <ScrollArea>
            <TreeInner>
              <KpiPanelProvider>
                {roots.map((root, index) => (
                  <OfferTreeNode key={root.offer.id ?? index} node={root} />
                ))}
              </KpiPanelProvider>
            </TreeInner>
          </ScrollArea>
        </ExpansionProvider>
      </Root>
    </MainOfferProvider>
  )
}
